/*
 * <email-capture> — one email control for two Klaviyo destinations:
 *
 *   data-mode="launch"   subscribes to the shared pre-launch list, tagged with
 *                        pre_launch_product so one Klaviyo flow segments by product
 *   data-mode="restock"  a back-in-stock subscription bound to a single variant
 *
 * Restock is re-pointed by <buy-box> writing data-variant-id on every sold-out
 * entry, which is why this replaces Klaviyo's own back-in-stock widget: that
 * widget has no public re-init, so it stayed pinned to whichever variant was
 * resolved at page load.
 *
 * Marketing consent is enforced here, in code. Cookiebot runs in auto
 * blockingmode, where blocking comes from its domain scan and not from our
 * markup — a data-cookieconsent attribute on a first-party script does nothing,
 * and Cookiebot never inspects fetch. Nothing is sent without consent.
 */

/* Everything is inside the guard, not just define(): a page can load this asset
   more than once — the quick-add modal injects a whole MainProduct section with
   its scripts re-created — and a second evaluation of a top-level
   `class`/`const` throws SyntaxError before define() is ever reached. */
if (!customElements.get('email-capture')) {
  const KLAVIYO_COMPANY_ID = 'WKej9t';
  /* Klaviyo supports each dated revision for two years — one stable, one
     deprecated — then retires it and silently falls forward to the next
     oldest, so an expired pin does not fail loudly, it just starts running
     on a revision nobody chose.
     https://developers.klaviyo.com/en/docs/api_versioning_and_deprecation_policy */
  const KLAVIYO_REVISION = '2026-07-15';
  const KLAVIYO_LAUNCH_LIST_ID = 'SnadY2';

  const SUBSCRIBE_ENDPOINT = `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;
  const BACK_IN_STOCK_ENDPOINT = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;

  /* Kept distinct per mode, and 'coming_soon_signup:' is kept verbatim from the
     snippet this replaces — changing it would forget every existing subscriber
     and show them the form again. */
  const STORAGE_PREFIX = {
    launch: 'coming_soon_signup:',
    restock: 'notify_restock:',
  };

  class EmailCapture extends HTMLElement {
    /* The variant binding is an attribute rather than a method call because two
       writers set it: <buy-box> writes it synchronously the moment a size is
       picked, and the section fetch confirms it a network round trip later. One
       observed attribute makes both paths the same write.

       The mode is observed for the same reason. One control serves both
       destinations, and which one a sold-out size belongs to is decided after
       render: a successor that has not launched has no variant to restock, so
       <buy-box> flips this control to the launch list on that size and back
       again on the next. Re-read rather than latched at upgrade, or the flip
       would keep posting to the endpoint the markup happened to open on. */
    static get observedAttributes() {
      return ['data-variant-id', 'data-mode', 'data-product-handle'];
    }

    /* <buy-box> resolves the whole binding at once and writes it as separate
       attributes in one tick — target, mode, and both pieces of copy. Rendering
       per attribute would draw the new mode against the previous mode's target
       and reset the field two or three times over; the microtask runs once,
       after the last write, and still before the frame is painted. */
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      /* Fires during upgrade, before connectedCallback has found the form. */
      if (!this.form) return;
      if (this.rebindQueued) return;

      this.rebindQueued = true;
      Promise.resolve().then(() => {
        this.rebindQueued = false;
        this.mode = this.readMode();
        this.reset();
        this.render();
      });
    }

    /* Anything but 'restock' is the launch list, matching the snippet's own
       default: the two destinations are exhaustive and launch is the safer
       fallback — it subscribes to a list rather than to a variant guess. */
    readMode() {
      return this.dataset.mode === 'restock' ? 'restock' : 'launch';
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="email"]');
      this.button = this.querySelector('button[type="submit"]');
      this.label = this.querySelector('[data-email-capture-label]');
      this.message = this.querySelector('[data-email-capture-message]');

      if (!this.form || !this.input || !this.button) return;

      this.mode = this.readMode();
      this.submitting = false;

      this.onSubmit = this.handleSubmit.bind(this);
      this.onConsentChange = this.render.bind(this);

      this.form.addEventListener('submit', this.onSubmit);

      /* Both events, matching klaviyo-consent-fallback.liquid: OnConsentReady
         fires on every load once the state is known, OnAccept only on a change. */
      window.addEventListener('CookiebotOnConsentReady', this.onConsentChange);
      window.addEventListener('CookiebotOnAccept', this.onConsentChange);

      /* Deliberately NO variant:section-rendered listener: <buy-box> writes
         data-variant-id synchronously on every path that opens or re-points the
         capture (bindCapture on sold-out entry and in showCapture), so it is the
         one writer. The document event also carries renders from OTHER sections —
         a quick-add modal's variant change would repoint this subscription at a
         different product's variant. */

      this.render();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('CookiebotOnConsentReady', this.onConsentChange);
      window.removeEventListener('CookiebotOnAccept', this.onConsentChange);
    }

    get subscriptionKey() {
      const id = this.mode === 'restock' ? this.dataset.variantId : this.dataset.productHandle;
      return id ? STORAGE_PREFIX[this.mode] + id : '';
    }

    hasMarketingConsent() {
      return !!(window.Cookiebot && window.Cookiebot.consent && window.Cookiebot.consent.marketing);
    }

    /* Cookiebot absent entirely (blocked, or still loading) is NOT the same as
       consent denied: there is no dialog to open, so offering "Allow cookies"
       would be a button that does nothing when pressed. */
    canOpenConsentDialog() {
      return !!(window.Cookiebot && typeof window.Cookiebot.show === 'function');
    }

    readSubscribed() {
      const key = this.subscriptionKey;
      if (!key) return false;
      try {
        return localStorage.getItem(key) === '1';
      } catch (error) {
        return false;
      }
    }

    writeSubscribed(key) {
      if (!key) return;
      try {
        localStorage.setItem(key, '1');
      } catch (error) {
        /* Private mode / quota. The subscription itself succeeded, so the only
           cost is that the form reappears on the next visit. */
      }
    }

    reset() {
      this.submitting = false;
      this.input.hidden = false;
      this.button.hidden = false;
      this.button.classList.remove('email-capture__submit--consent');
      this.setMessage('', '');
    }

    render() {
      if (this.submitting) return;

      if (this.readSubscribed()) {
        this.showSuccess();
        return;
      }

      this.input.hidden = false;
      this.button.hidden = false;
      this.button.disabled = false;

      if (this.hasMarketingConsent()) {
        this.setLabel(this.button.dataset.defaultLabel);
        this.setMessage('', '');
        this.button.classList.remove('email-capture__submit--consent');
        return;
      }

      this.setMessage(this.dataset.consentMessage, 'consent');

      if (this.canOpenConsentDialog()) {
        this.setLabel(this.dataset.consentButton);
        this.button.classList.add('email-capture__submit--consent');
      } else {
        this.setLabel(this.button.dataset.defaultLabel);
        this.button.disabled = true;
        this.button.classList.remove('email-capture__submit--consent');
      }
    }

    setLabel(text) {
      if (this.label && text) this.label.textContent = text;
    }

    setMessage(text, variant) {
      if (!this.message) return;
      if (!text) {
        this.message.hidden = true;
        this.message.textContent = '';
        return;
      }
      this.message.textContent = text;
      this.message.className = `email-capture__message email-capture__message--${variant}`;
      this.message.hidden = false;
    }

    /* Restores the button before hiding it. Leaving it disabled and still reading
       the pending label would strand that state on the element for whatever
       un-hides it next — a variant change, in restock mode. */
    showSuccess() {
      this.button.disabled = false;
      this.setLabel(this.button.dataset.defaultLabel);
      this.input.hidden = true;
      this.button.hidden = true;
      this.setMessage(this.dataset.successMessage, 'success');
    }

    handleSubmit(event) {
      event.preventDefault();

      /* Asked here, never carried over from render(): withdrawing marketing
         consent fires CookiebotOnDecline, which nothing re-renders on, so a form
         drawn under consent is still on screen after the consent is gone.
         Re-rendering first is what tells the shopper why nothing was sent. */
      if (!this.hasMarketingConsent()) {
        this.render();
        if (this.canOpenConsentDialog()) window.Cookiebot.show();
        return;
      }

      if (!this.input.checkValidity()) {
        this.input.reportValidity();
        return;
      }

      this.subscribe(this.input.value);
    }

    subscribe(email) {
      /* Snapshotted with the key below and for the same reason: <buy-box> can
         flip this control between the restock and launch destinations while the
         request is in flight, and the response belongs to the mode it was sent
         in — a launch signup owes a lead event whether or not the shopper has
         moved on to a size that does not. */
      const mode = this.mode;
      const payload = mode === 'restock' ? this.restockPayload(email) : this.launchPayload(email);
      if (!payload) {
        this.setMessage(this.dataset.errorMessage, 'error');
        return;
      }

      this.submitting = true;
      this.button.disabled = true;
      this.setLabel(this.button.dataset.pendingLabel);
      this.setMessage('', '');

      /* The subscription goes to the variant bound NOW; <buy-box> can re-point
         the capture while the request is in flight, and resolving against the
         key of that later variant would mark the wrong size as subscribed. */
      const key = this.subscriptionKey;

      fetch(payload.url, {
        method: 'POST',
        headers: {
          'Content-Type': payload.contentType,
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(payload.body),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Klaviyo ${mode} subscribe failed: ${response.status}`);
          this.submitting = false;
          this.writeSubscribed(key);
          /* A launch signup is a marketing lead; a restock alert is not. */
          if (mode === 'launch' && window.myrqvistLead) {
            window.myrqvistLead.track('launch_signup', { email });
          }
          if (key === this.subscriptionKey) {
            this.showSuccess();
          } else {
            /* Success, but for the size bound at submit — the shopper is looking
               at a different one now; render its true (unsubscribed) state. */
            this.render();
          }
        })
        .catch((error) => {
          console.error('email-capture: subscribe failed', error);
          this.submitting = false;
          /* render() is the one place that derives the label and the
             --consent class together from the current consent state — call
             it rather than setting the default label directly, or a consent
             withdrawal that happened while this request was in flight leaves
             the button in a mismatched state: --consent styling under a
             default-label reset. */
          this.render();
          /* render() already drew success if another tab/instance subscribed
             this same key while this request was in flight — don't clobber
             that message with this request's own failure. */
          if (!this.readSubscribed()) this.setMessage(this.dataset.errorMessage, 'error');
        });
    }

    launchPayload(email) {
      const handle = this.dataset.productHandle;
      if (!handle) return null;

      return {
        url: SUBSCRIBE_ENDPOINT,
        contentType: 'application/json',
        body: {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: `Coming Soon — ${handle}`,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    properties: { pre_launch_product: handle },
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: KLAVIYO_LAUNCH_LIST_ID } },
            },
          },
        },
      };
    }

    restockPayload(email) {
      const variantId = this.dataset.variantId;
      if (!variantId) return null;

      return {
        url: BACK_IN_STOCK_ENDPOINT,
        contentType: 'application/vnd.api+json',
        body: {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        },
      };
    }
  }

  customElements.define('email-capture', EmailCapture);
}
